import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { MoreVertical, Plus, Paperclip, Calendar, Pencil, Trash2, ChevronLeft, ChevronRight, CheckCircle, RotateCcw, ArrowRightCircle, FolderInput } from "lucide-react";
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  defaultDropAnimationSideEffects,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import type { Task, TaskStatusDetail } from "../../utils/taskHelpers";
import {
  getPriorityBadgeStyles,
  getAssigneeGradient,
  getUserInitials,
  getPriorityAccentColor,
  formatDateShort,
} from "../../utils/taskHelpers";
import DeleteColumnDialog from "./DeleteColumnDialog";
import EditColumnDialog from "./EditColumnDialog";

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: "0" } },
  }),
};

// Helper per formattare la data da "2026-02-18" a "18/02/2026"
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

// Helper per verificare se un task è scaduto
const isOverdue = (task: Task): boolean => {
  if (!task.due_date) return false;
  if (task.status?.key === "COMPLETATO" || task.status?.key === "ANNULLATO") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(task.due_date);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

export type ColumnState = { count: number; hasMore: boolean; loading: boolean };

interface TaskKanbanViewProps {
  tasks: Task[];
  statusColumns: TaskStatusDetail[];
  // Stati per-colonna: count reale dal BE, hasMore per "Carica altri", loading in-flight.
  // Se undefined per una colonna, si usa tasks.length come fallback (no pulsante).
  columnStates?: Record<number, ColumnState>;
  onLoadMore?: (statusId: number) => void | Promise<unknown>;
  onOpenTask: (taskId: number) => void;
  onCreateTask: (status: string) => void;
  /**
   * Spostamento atomico (FE-5): il caller chiama `moveTask` con project/status/position.
   * Deve REJECT su errore: il componente ripristina lo snapshot salvato a drag start
   * e mostra toast. Su success il componente scarta lo snapshot.
   */
  onTaskMove: (
    taskId: number,
    toStatusId: number,
    toPosition: number,
    fromStatusId: number
  ) => Promise<void>;
  /**
   * Riordino colonne batch (FE-5). Singola chiamata a `reorderTaskColumns`.
   * Deve REJECT su errore: il componente ripristina l'ordine precedente.
   */
  onColumnOrderChange: (newOrder: TaskStatusDetail[]) => Promise<void>;
  onRenameStatus?: (statusId: number, newName: string) => Promise<{ success: boolean; error?: string }>;
  /**
   * Callback per aggiornare nome e colore di una colonna.
   */
  onUpdateColumn?: (statusId: number, name: string, color: string) => Promise<{ success: boolean; error?: string }>;
  /**
   * FE-6: callback per eliminazione colonna vuota (DELETE semplice).
   * Sostituisce onDeleteStatus con force=false.
   */
  onDeleteEmptyColumn?: (columnId: number) => Promise<{ success: boolean; error?: string }>;
  /**
   * FE-6: callback per move-and-delete (colonna piena con destinazione).
   * Sostituisce onDeleteStatus con force=true.
   */
  onMoveAndDeleteColumn?: (columnId: number, destinationId: number) => Promise<{ success: boolean; error?: string }>;
  /**
   * FE-6: colonne disponibili come destinazione per move-and-delete.
   * Passato dal container per popolare la select del DeleteColumnDialog.
   */
  availableColumns?: TaskStatusDetail[];
  onAddColumn?: () => Promise<{ success: boolean; newColumnId?: number; error?: string }>;
  onDeleteTask?: (taskId: number) => Promise<void>;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  /**
   * Apri MoveTaskDialog mode="column" sul task (FE-5 §8). Se omesso, la voce
   * "Sposta nella colonna..." non viene mostrata.
   */
  onMoveTaskToColumn?: (taskId: number) => void;
  /**
   * Apri MoveTaskDialog mode="project" sul task (FE-5 §8). Se omesso, la voce
   * "Sposta in un altro progetto" non viene mostrata.
   */
  onMoveTaskToProject?: (taskId: number) => void;
  isManager?: boolean;
  highlightedTaskId?: number | null;
}

// Group tasks by status ID (BUG-2.1 FIX: use numeric ID instead of string key)
// The backend TaskStatusRefSerializer returns status_detail with id but NOT key,
// so grouping by key discards tasks. Using id is the authoritative match.
type TasksByStatusId = Record<number, Task[]>;

export default function TaskKanbanView({
  tasks,
  statusColumns,
  columnStates,
  onLoadMore,
  onOpenTask,
  onCreateTask,
  onTaskMove,
  onColumnOrderChange,
  onRenameStatus,
  onUpdateColumn,
  onDeleteEmptyColumn,
  onMoveAndDeleteColumn,
  availableColumns,
  onAddColumn,
  onDeleteTask,
  onToggleCompleted,
  onMoveTaskToColumn,
  onMoveTaskToProject,
  isManager = false,
  highlightedTaskId,
}: TaskKanbanViewProps) {
  const [columnOrder, setColumnOrder] = useState<TaskStatusDetail[]>(statusColumns);
  // BUG-2.1 FIX: Use numeric status IDs for grouping instead of string keys
  const [localTasksByStatusId, setLocalTasksByStatusId] = useState<TasksByStatusId>({});

  // Stati per gestire menu, rinomina e eliminazione colonne.
  // BUG colonne: usiamo l'ID numerico autorevole (sempre presente dal BE) invece
  // della chiave tecnica ``key`` per l'identità React/UI. Con ``key`` mancante nel
  // payload, ``undefined === undefined`` apriva tutti i menu contemporaneamente.
  const [openMenuColumnId, setOpenMenuColumnId] = useState<number | null>(null);
  const [renamingColumnId, setRenamingColumnId] = useState<number | null>(null);
  const [pendingRenameColumnId, setPendingRenameColumnId] = useState<number | null>(null);

  // Toggle funzionale: garantisce che un solo menu alla volta possa essere aperto.
  const handleMenuToggle = (columnId: number) => {
    setOpenMenuColumnId(prev => (prev === columnId ? null : columnId));
  };

  // Stato AlertDialog delete colonna (sostituisce il pannello inline precedente)
  /**
   * FE-6: stato per DeleteColumnDialog con select destinazione.
   */
  const [deleteColumnDialog, setDeleteColumnDialog] = useState<{
    open: boolean;
    statusId?: number;
    columnName?: string;
    tasksCount: number;
  }>({ open: false, tasksCount: 0 });

  // Stato EditColumnDialog per modificare nome e colore
  const [editColumnDialog, setEditColumnDialog] = useState<{
    open: boolean;
    statusId?: number;
    columnName?: string;
    columnColor?: string;
  }>({ open: false });

  // Stati per gestire menu e eliminazione task
  const [openMenuTaskId, setOpenMenuTaskId] = useState<string | null>(null);
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<number | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  /**
   * FE-5 §8: snapshot immutabile di `localTasksByStatusId` salvato a drag start.
   * Usato per ripristinare lo stato visuale se la `moveTask` fallisce.
   * Tipo deep clone via JSON (i task sono serializzabili).
   */
  const dragSnapshotRef = useRef<TasksByStatusId | null>(null);
  /**
   * FE-5 §8: snapshot dell'ordine colonne salvato a drag start della colonna.
   * Per ripristinare se la `reorderTaskColumns` fallisce.
   */
  const columnOrderSnapshotRef = useRef<TaskStatusDetail[] | null>(null);
  /**
   * FE-5 §8: id del task in fase di spostamento (pending). Disabilita un
   * secondo drag della stessa card finché la richiesta non è completata.
   */
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(null);
  /**
   * FE-5 §8: riordino colonne in corso. Disabilita ulteriori riordini.
   */
  const [, setPendingColumnReorder] = useState(false);

  // Ref e stati per scroll orizzontale con frecce
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Aggiorna stato scroll
  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    setHasOverflow(scrollWidth > clientWidth);
  };

  // Funzione per scrollare
  const scrollContainer = (direction: -1 | 1) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: direction * 304, behavior: 'smooth' });
  };

  // Scroll-into-view sul task evidenziato (es. dopo click su notifica)
  useEffect(() => {
    if (!highlightedTaskId) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${highlightedTaskId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [highlightedTaskId, localTasksByStatusId]);

  // Update column order when statusColumns prop changes
  useEffect(() => {
    setColumnOrder(statusColumns);

    // Se c'è una colonna in attesa di rinomina, attiva la rinomina.
    // BUG colonne: pendingRenameColumnId è già un ID numerico autorevole,
    // non serve convertirlo in key.
    if (pendingRenameColumnId) {
      const exists = statusColumns.some(c => c.id === pendingRenameColumnId);
      if (exists) {
        setRenamingColumnId(pendingRenameColumnId);
        setPendingRenameColumnId(null);
      }
    }
  }, [statusColumns, pendingRenameColumnId]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<"column" | "task" | null>(null);
  // Use refs for synchronous tracking during drag operations (avoids React async state issues)
  const originalTaskStatusRef = useRef<string | null>(null);
  const lastDragOverColumnRef = useRef<string | null>(null);

  // Group tasks by status ID (BUG-2.1 FIX: use numeric ID instead of string key)
  useEffect(() => {
    // Non ricostruire durante il drag — il drag gestisce lo stato locale
    if (activeId) return;

    const tasksByStatusId: TasksByStatusId = {};

    // Guard against invalid statusColumns
    if (!statusColumns || !Array.isArray(statusColumns)) {
      return;
    }

    // Initialize map with column IDs
    statusColumns.forEach(col => {
      // Guard against invalid column objects
      if (col && col.id != null) {
        tasksByStatusId[col.id] = [];
      }
    });

    // Group tasks by status_detail.id (the authoritative field from backend)
    tasks.forEach(task => {
      const statusId = task.status?.id ?? task.status_detail?.id;
      if (statusId !== undefined && tasksByStatusId[statusId]) {
        tasksByStatusId[statusId].push(task);
      }
    });

    setLocalTasksByStatusId(tasksByStatusId);
    // NB: activeId NON va nelle deps. Il guard `if (activeId) return;` sopra
    // serve solo per il caso in cui la prop `tasks` cambi durante un drag
    // (es. refetch parent / websocket). Se mettessimo activeId nelle deps,
    // l'effect si ri-eseguirebbe a fine drag (quando activeId torna a null)
    // riscrivendo lo stato locale dall'ordine OLD della prop `tasks` e
    // facendo "tornare" la card alla posizione originale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, statusColumns]);

  // Aggiorna stato scroll al mount, al resize, e quando cambiano le colonne
  useEffect(() => {
    updateScrollState();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollState);
    }

    window.addEventListener('resize', updateScrollState);

    return () => {
      if (container) {
        container.removeEventListener('scroll', updateScrollState);
      }
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  // BUG-2.1: mapping between status keys and IDs for drag operations
  // Drag uses keys (for dnd-kit), but task grouping uses IDs (authoritative)
  const keyToIdMap = useMemo(() => {
    const map: Record<string, number> = {};
    statusColumns.forEach(col => {
      if (col.key && col.id != null) {
        map[col.key] = col.id;
      }
    });
    return map;
  }, [statusColumns]);

  // Aggiorna quando cambiano le colonne
  useEffect(() => {
    // Piccolo delay per permettere al DOM di aggiornarsi
    const timer = setTimeout(updateScrollState, 100);
    return () => clearTimeout(timer);
  }, [columnOrder]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.id as string;
    // FE-5 §8: disabilita secondo drag se già pending per questo task.
    if (pendingTaskId !== null && String(pendingTaskId) === id) return;

    setActiveId(id);
    lastDragOverColumnRef.current = null;

    // Check if dragging a column or task
    if (columnOrder.some(col => col.key === id)) {
      setActiveType("column");
      originalTaskStatusRef.current = null;
      // FE-5: snapshot ordine colonne per rollback.
      columnOrderSnapshotRef.current = columnOrder;
    } else {
      setActiveType("task");
      // FE-5: snapshot immutabile dello stato task per rollback su errore move.
      // Deep clone via JSON: i task sono serializzabili (no Date/function).
      try {
        dragSnapshotRef.current = JSON.parse(JSON.stringify(localTasksByStatusId));
      } catch (err) {
        console.error("Snapshot serialization failed:", err);
        dragSnapshotRef.current = localTasksByStatusId;
      }
      // BUG-2.1 FIX: Save original status ID (not key) for tracking
      // Find which column (by ID) contains this task
      for (const [statusId, statusTasks] of Object.entries(localTasksByStatusId)) {
        if (statusTasks.some((t) => t.id.toString() === id)) {
          // Track by key for dnd-kit compatibility, but the actual grouping uses ID
          const statusKey = statusColumns.find(c => c.id === Number(statusId))?.key;
          originalTaskStatusRef.current = statusKey || null;
          lastDragOverColumnRef.current = statusKey || null; // Initialize with current column
          break;
        }
      }
    }
  }

  function handleDragOver(event: DragOverEvent) {
    // handleDragOver gestisce solo il drag visuale dei task
    // Per le colonne, @dnd-kit gestisce automaticamente il riordino
    if (activeType !== "task") return;

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Use the ref to track where the task CURRENTLY is (avoids React async state issues)
    const currentTaskColumnKey = lastDragOverColumnRef.current;
    if (!currentTaskColumnKey) return;

    // Convert key to ID for task group operations
    const currentTaskColumnId = keyToIdMap[currentTaskColumnKey];
    if (currentTaskColumnId === undefined) return;

    // Determine target status — priorità ai TASK rispetto alle colonne, perché
    // con collision detection sovrapposte la colonna può "vincere" sul task hover-ato.
    let targetStatusKey: string | null = null;

    if (overId === activeId) {
      // Hovering over the dragged task itself - use the current tracked column
      targetStatusKey = currentTaskColumnKey;
    } else {
      // Prima controlla se overId è un TASK (priorità sui task)
      let foundAsTask = false;
      for (const [statusId, tasks] of Object.entries(localTasksByStatusId)) {
        if (tasks.some((t) => t.id.toString() === overId)) {
          // Convert ID back to key for tracking
          targetStatusKey = statusColumns.find(c => c.id === Number(statusId))?.key || null;
          foundAsTask = true;
          break;
        }
      }

      // Solo se NON è un task, controlla se è una colonna (drop su area vuota).
      // Accetta sia la column key "pura" (sortable della colonna) sia l'id
      // "col-<key>" del droppable dedicato della lista task (vedi KanbanColumn).
      if (!foundAsTask) {
        if (columnOrder.some(col => col.key === overId)) {
          targetStatusKey = overId;
        } else if (typeof overId === 'string' && overId.startsWith('col-')) {
          const key = overId.slice(4);
          if (columnOrder.some(col => col.key === key)) {
            targetStatusKey = key;
          }
        }
      }
    }

    if (!targetStatusKey) return;

    // Convert target key to ID for operations
    const targetStatusId = keyToIdMap[targetStatusKey];
    if (targetStatusId === undefined) return;

    // Always update the ref to track the current target column
    lastDragOverColumnRef.current = targetStatusKey;

    // Stessa colonna: riordina con arrayMove
    if (currentTaskColumnId === targetStatusId) {
      if (overId === activeId) return;
      setLocalTasksByStatusId((prev) => {
        const list = [...(prev[currentTaskColumnId] ?? [])];
        const oldIndex = list.findIndex((t) => t.id.toString() === activeId);
        const newIndex = list.findIndex((t) => t.id.toString() === overId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
        return { ...prev, [currentTaskColumnId]: arrayMove(list, oldIndex, newIndex) };
      });
      return;
    }

    // Cross-column: rimuovi da source, inserisci alla posizione hover in target (no push in coda)
    setLocalTasksByStatusId((prev) => {
      const sourceList = [...(prev[currentTaskColumnId] ?? [])];
      const targetList = [...(prev[targetStatusId] ?? [])];

      const taskIndex = sourceList.findIndex((t) => t.id.toString() === activeId);
      if (taskIndex === -1) return prev;

      const [oldTask] = sourceList.splice(taskIndex, 1);
      // FE-5 §8: NON mutare il task in place. Crea nuovo oggetto con status aggiornato.
      const newStatus = columnOrder.find(col => col.key === targetStatusKey);
      const task = newStatus
        ? { ...oldTask, status: { ...newStatus } }
        : { ...oldTask };

      const overIndex = targetList.findIndex((t) => t.id.toString() === overId);
      if (overIndex >= 0) {
        targetList.splice(overIndex, 0, task);
      } else {
        targetList.push(task);
      }

      return {
        ...prev,
        [currentTaskColumnId]: sourceList,
        [targetStatusId]: targetList,
      };
    });
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { over } = event;
    setActiveId(null);
    const currentType = activeType;
    setActiveType(null);

    // Read refs synchronously before clearing
    const savedOriginalStatusKey = originalTaskStatusRef.current;
    const savedLastDragOverColumnKey = lastDragOverColumnRef.current;
    originalTaskStatusRef.current = null;
    lastDragOverColumnRef.current = null;

    if (!over) {
      // Drag annullato: ripristina snapshot (se salvato).
      if (dragSnapshotRef.current) {
        setLocalTasksByStatusId(dragSnapshotRef.current);
        dragSnapshotRef.current = null;
      }
      columnOrderSnapshotRef.current = null;
      return;
    }

    const activeId = event.active.id as string;
    const overId = (over as any).id as string;

    // Handle column reordering - for columns, same id means no move
    if (currentType === "column") {
      if (activeId === overId) {
        columnOrderSnapshotRef.current = null;
        return;
      }
      const snapshot = columnOrderSnapshotRef.current ?? columnOrder;
      columnOrderSnapshotRef.current = null;
      const oldIndex = columnOrder.findIndex(col => col.key === activeId);
      const newIndex = columnOrder.findIndex(col => col.key === overId);

      if (oldIndex === -1 || newIndex === -1) return;

      const newCols = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newCols);
      setPendingColumnReorder(true);
      try {
        // Singola action batch `reorderTaskColumns`. Su error il caller deve reject.
        await onColumnOrderChange(newCols);
      } catch (err) {
        console.error("Reorder colonne fallito, ripristino ordine precedente:", err);
        setColumnOrder(snapshot);
        toast.error("Non è stato possibile salvare il riordino delle colonne. Riprova.");
      } finally {
        setPendingColumnReorder(false);
      }
      return;
    }

    // Handle task movement
    if (currentType === "task") {
      const activeStatusKey = savedOriginalStatusKey;
      const targetStatusKey = savedLastDragOverColumnKey;

      if (!activeStatusKey || !targetStatusKey) {
        dragSnapshotRef.current = null;
        return;
      }

      const taskIdNum = Number(activeId);
      // Convert keys to IDs for operations
      const targetStatusId = keyToIdMap[targetStatusKey];
      const activeStatusId = keyToIdMap[activeStatusKey];

      if (targetStatusId === undefined || activeStatusId === undefined) {
        console.warn('[Kanban] onTaskMove skipped: could not resolve status IDs from keys');
        dragSnapshotRef.current = null;
        return;
      }

      // Posizione finale del task nella colonna target (visuale).
      const targetList = localTasksByStatusId[targetStatusId] ?? [];
      const targetPosition = targetList.findIndex((t) => String(t.id) === activeId);

      const fromCol = columnOrder.find((c) => c.key === activeStatusKey);
      const toCol = columnOrder.find(col => col.key === targetStatusKey);

      // No-op: stessa colonna, stessa posizione (nessun movimento reale).
      if (activeStatusId === targetStatusId && targetPosition <= 0) {
        // Anche se è il primo elemento incolonnato senza spostamento reale,
        // chiama moveTask solo se c'è stato un reorder effettivo.
        const originalList = dragSnapshotRef.current?.[activeStatusId] ?? [];
        const originalPos = originalList.findIndex((t) => String(t.id) === activeId);
        if (originalPos === targetPosition || targetPosition < 0) {
          dragSnapshotRef.current = null;
          return;
        }
      }

      if (!toCol || !fromCol || targetPosition < 0) {
        console.warn('[Kanban] onTaskMove skipped: could not resolve columns', { activeStatusKey, targetStatusKey });
        // Ripristina snapshot per coerenza visuale.
        if (dragSnapshotRef.current) {
          setLocalTasksByStatusId(dragSnapshotRef.current);
          dragSnapshotRef.current = null;
        }
        return;
      }

      // FE-5 §8: disabilita doppio drag della stessa card durante la richiesta.
      setPendingTaskId(taskIdNum);
      try {
        // Singola chiamata atomica `moveTask` con position. Il caller decide
        // project/status dallo scope corrente; qui passiamo solo IDs + position.
        await onTaskMove(taskIdNum, toCol.id, targetPosition, fromCol.id);
        // Successo: scarta snapshot. La response backend è autoritativa e il
        // caller ha già aggiornato tasks/tasksByColumn.
        dragSnapshotRef.current = null;
      } catch (err) {
        console.error("Spostamento fallito, ripristino snapshot:", err);
        // FE-5 §8: rollback immediato dello stato visuale.
        if (dragSnapshotRef.current) {
          setLocalTasksByStatusId(dragSnapshotRef.current);
          dragSnapshotRef.current = null;
        }
        toast.error("Non è stato possibile salvare lo spostamento. Riprova.");
        // Il caller esegue già refetch delle colonne coinvolte (gestito nel suo catch).
      } finally {
        setPendingTaskId(null);
      }
    }
  }

  const activeTask =
    activeId && activeType === "task"
      ? Object.values(localTasksByStatusId)
          .flat()
          .find((t) => t.id.toString() === activeId)
      : null;

  const activeColumn = activeId && activeType === "column"
    ? columnOrder.find(col => col.key === activeId)
    : null;

  // Handler per eliminare una task
  const handleConfirmDeleteTask = async () => {
    if (deleteConfirmTaskId && onDeleteTask) {
      setIsDeletingTask(true);
      try {
        await onDeleteTask(deleteConfirmTaskId);
        setDeleteConfirmTaskId(null);
      } catch (error) {
        console.error("Errore eliminazione task:", error);
      } finally {
        setIsDeletingTask(false);
      }
    }
  };

  // Chiudi menu task quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuTaskId) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-task-menu]')) {
          setOpenMenuTaskId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuTaskId]);

  // Nota: non c'è un listener "click outside" globale per i menu colonna.
  // Ogni KanbanColumn registra il proprio handler locale su ``menuRef``
  // (che include sia il trigger button che il dropdown): così il toggle
  // funziona correttamente (secondo click sullo stesso pulsante chiude il menu)
  // e un click su una colonna diversa chiude il menu corrente tramite
  // l'handler locale + il toggle funzionale su ``openMenuColumnId``.

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="w-full relative">
        {/* Frecce di navigazione scroll */}
        {hasOverflow && (
          <div className="flex justify-end mb-3">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => scrollContainer(-1)}
                disabled={!canScrollLeft}
                className={`w-8 h-8 rounded-lg border grid place-items-center transition
                  ${canScrollLeft
                    ? 'border-[var(--hexa-border)] bg-[var(--hexa-surface)] text-[var(--hexa-text-muted)] hover:bg-[var(--hexa-surface-elevated)] hover:text-[var(--hexa-text-primary)] hover:border-[var(--hexa-border-subtle)]'
                    : 'border-[var(--hexa-border-subtle)] bg-transparent text-[var(--hexa-text-muted)] opacity-30 cursor-default'
                  }`}
                aria-label="Scorri a sinistra"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollContainer(1)}
                disabled={!canScrollRight}
                className={`w-8 h-8 rounded-lg border grid place-items-center transition
                  ${canScrollRight
                    ? 'border-[var(--hexa-border)] bg-[var(--hexa-surface)] text-[var(--hexa-text-muted)] hover:bg-[var(--hexa-surface-elevated)] hover:text-[var(--hexa-text-primary)] hover:border-[var(--hexa-border-subtle)]'
                    : 'border-[var(--hexa-border-subtle)] bg-transparent text-[var(--hexa-text-muted)] opacity-30 cursor-default'
                  }`}
                aria-label="Scorri a destra"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <SortableContext items={columnOrder.map(c => c.key)} strategy={horizontalListSortingStrategy}>
          <div ref={scrollContainerRef} aria-label="Colonne Kanban" className="flex min-w-0 gap-4 overflow-x-auto snap-x snap-proximity pb-4 scrollbar-hexa">
            {columnOrder.map((col) => {
              // BUG-2.1 FIX: Use col.id instead of col.key for task lookup
              const colTasks = localTasksByStatusId[col.id] ?? [];
              return (
                <SortableColumn
                  key={col.id}
                  status={col}
                  tasks={colTasks}
                  onTaskClick={onOpenTask}
                  onCreateTask={onCreateTask}
                  isManager={isManager}
                  isMenuOpen={openMenuColumnId === col.id}
                  onMenuToggle={() => handleMenuToggle(col.id)}
                  onMenuClose={() => setOpenMenuColumnId(null)}
                  isRenaming={renamingColumnId === col.id}
                  onStartRename={() => {
                    setOpenMenuColumnId(null);
                    setRenamingColumnId(col.id);
                  }}
                  onCancelRename={() => setRenamingColumnId(null)}
                  onSaveRename={async (newName: string) => {
                    if (onRenameStatus) {
                      const result = await onRenameStatus(col.id, newName);
                      if (result.success) {
                        setRenamingColumnId(null);
                      }
                      return result;
                    }
                    return { success: false, error: "Funzione non disponibile" };
                  }}
                  onAddColumn={async () => {
                    setOpenMenuColumnId(null);
                    if (onAddColumn) {
                      const result = await onAddColumn();
                      if (result.success && result.newColumnId) {
                        // Imposta l'ID della colonna da rinominare quando apparirà
                        setPendingRenameColumnId(result.newColumnId);
                      }
                    }
                  }}
                  onEditColumn={() => {
                    setOpenMenuColumnId(null);
                    setEditColumnDialog({
                      open: true,
                      statusId: col.id,
                      columnName: col.name,
                      columnColor: col.color,
                    });
                  }}
                  onDeleteColumn={() => {
                    setOpenMenuColumnId(null);
                    const count = columnStates?.[col.id]?.count ?? 0;
                    setDeleteColumnDialog({
                      open: true,
                      statusId: col.id,
                      columnName: col.name,
                      tasksCount: count,
                    });
                  }}
                  isLastColumn={statusColumns.length <= 1}
                  openMenuTaskId={openMenuTaskId}
                  onTaskMenuToggle={(taskId) => setOpenMenuTaskId(openMenuTaskId === taskId ? null : taskId)}
                  onTaskMenuClose={() => setOpenMenuTaskId(null)}
                  onDeleteTaskClick={(taskId) => {
                    setOpenMenuTaskId(null);
                    setDeleteConfirmTaskId(taskId);
                  }}
                  onToggleCompleted={onToggleCompleted}
                  onMoveTaskToColumn={onMoveTaskToColumn}
                  onMoveTaskToProject={onMoveTaskToProject}
                  pendingTaskId={pendingTaskId}
                  highlightedTaskId={highlightedTaskId}
                  columnState={columnStates?.[col.id]}
                  onLoadMore={onLoadMore ? () => onLoadMore(col.id) : undefined}
                />
              );
            })}
          </div>
        </SortableContext>

      </div>

      <DragOverlay dropAnimation={dropAnimation}>
        {activeTask ? (
          <div className="rotate-3 opacity-90">
            <TaskCard task={activeTask} onTaskClick={() => {}} />
          </div>
        ) : null}
        {activeColumn ? (
          <div className="rotate-2 opacity-90 w-80">
            <KanbanColumnHeader status={activeColumn} taskCount={0} />
          </div>
        ) : null}
      </DragOverlay>

      {/* AlertDialog per conferma eliminazione task */}
      <AlertDialog open={deleteConfirmTaskId !== null} onOpenChange={(open) => !open && setDeleteConfirmTaskId(null)}>
        <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--hexa-text-primary)]">Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
              Sei sicuro di voler eliminare questa task? Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeletingTask}
              className="bg-[var(--hexa-surface-elevated)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteTask}
              disabled={isDeletingTask}
              className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
            >
              {isDeletingTask ? "Eliminazione..." : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FE-6: DeleteColumnDialog per move-and-delete (sostituisce il vecchio AlertDialog con force) */}
      <DeleteColumnDialog
        open={deleteColumnDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteColumnDialog({ open: false, tasksCount: 0 });
          }
        }}
        columnId={deleteColumnDialog.statusId}
        columnName={deleteColumnDialog.columnName}
        tasksCount={deleteColumnDialog.tasksCount}
        availableColumns={(availableColumns ?? statusColumns).filter(c => c.id !== deleteColumnDialog.statusId)}
        onDeleteEmpty={async (columnId) => {
          if (!onDeleteEmptyColumn) return { success: false, error: "Funzione non disponibile" };
          return onDeleteEmptyColumn(columnId);
        }}
        onMoveAndDelete={async (columnId, destinationId) => {
          if (!onMoveAndDeleteColumn) return { success: false, error: "Funzione non disponibile" };
          return onMoveAndDeleteColumn(columnId, destinationId);
        }}
      />

      {/* EditColumnDialog per modificare nome e colore */}
      <EditColumnDialog
        open={editColumnDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setEditColumnDialog({ open: false });
          }
        }}
        columnId={editColumnDialog.statusId}
        columnName={editColumnDialog.columnName}
        columnColor={editColumnDialog.columnColor}
        onSave={async (columnId, name, color) => {
          if (!onUpdateColumn) return { success: false, error: "Funzione non disponibile" };
          return onUpdateColumn(columnId, name, color);
        }}
      />
    </DndContext>
  );
}

function SortableColumn({
  status,
  tasks,
  onTaskClick,
  onCreateTask,
  isManager,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  isRenaming,
  onStartRename,
  onCancelRename,
  onSaveRename,
  onAddColumn,
  onEditColumn,
  onDeleteColumn,
  isLastColumn,
  openMenuTaskId,
  onTaskMenuToggle,
  onTaskMenuClose,
  onDeleteTaskClick,
  onToggleCompleted,
  onMoveTaskToColumn,
  onMoveTaskToProject,
  pendingTaskId,
  highlightedTaskId,
  columnState,
  onLoadMore,
}: {
  status: TaskStatusDetail;
  tasks: Task[];
  onTaskClick: (taskId: number) => void;
  onCreateTask: (status: string) => void;
  isManager: boolean;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  isRenaming: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onSaveRename: (newName: string) => Promise<{ success: boolean; error?: string }>;
  onAddColumn: () => void;
  onEditColumn: () => void;
  onDeleteColumn: () => void;
  isLastColumn: boolean;
  openMenuTaskId: string | null;
  onTaskMenuToggle: (taskId: string) => void;
  onTaskMenuClose: () => void;
  onDeleteTaskClick: (taskId: number) => void;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  onMoveTaskToColumn?: (taskId: number) => void;
  onMoveTaskToProject?: (taskId: number) => void;
  pendingTaskId?: number | null;
  highlightedTaskId?: number | null;
  columnState?: ColumnState;
  onLoadMore?: () => void | Promise<unknown>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: status.key,
    data: {
      type: "column",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-[min(280px,calc(100vw-48px))] sm:w-[210px] min-w-0 snap-start flex-shrink-0">
      <KanbanColumn
        status={status}
        tasks={tasks}
        onTaskClick={onTaskClick}
        onCreateTask={onCreateTask}
        columnListeners={listeners}
        columnAttributes={attributes}
        isManager={isManager}
        isMenuOpen={isMenuOpen}
        onMenuToggle={onMenuToggle}
        onMenuClose={onMenuClose}
        isRenaming={isRenaming}
        onStartRename={onStartRename}
        onCancelRename={onCancelRename}
        onSaveRename={onSaveRename}
        onAddColumn={onAddColumn}
        onEditColumn={onEditColumn}
        onDeleteColumn={onDeleteColumn}
        isLastColumn={isLastColumn}
        openMenuTaskId={openMenuTaskId}
        onTaskMenuToggle={onTaskMenuToggle}
        onTaskMenuClose={onTaskMenuClose}
        onDeleteTaskClick={onDeleteTaskClick}
        onToggleCompleted={onToggleCompleted}
        onMoveTaskToColumn={onMoveTaskToColumn}
        onMoveTaskToProject={onMoveTaskToProject}
        pendingTaskId={pendingTaskId}
        highlightedTaskId={highlightedTaskId}
        columnState={columnState}
        onLoadMore={onLoadMore}
      />
    </div>
  );
}

function KanbanColumn({
  status,
  tasks,
  onTaskClick,
  onCreateTask,
  columnListeners,
  columnAttributes,
  isManager,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  isRenaming,
  onStartRename,
  onCancelRename,
  onSaveRename,
  onAddColumn,
  onEditColumn,
  onDeleteColumn,
  isLastColumn,
  openMenuTaskId,
  onTaskMenuToggle,
  onTaskMenuClose,
  onDeleteTaskClick,
  onToggleCompleted,
  onMoveTaskToColumn,
  onMoveTaskToProject,
  pendingTaskId,
  highlightedTaskId,
  columnState,
  onLoadMore,
}: {
  status: TaskStatusDetail;
  tasks: Task[];
  onTaskClick: (taskId: number) => void;
  onCreateTask: (status: string) => void;
  columnListeners?: any;
  columnAttributes?: any;
  isManager: boolean;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  isRenaming: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onSaveRename: (newName: string) => Promise<{ success: boolean; error?: string }>;
  onAddColumn: () => void;
  onEditColumn: () => void;
  onDeleteColumn: () => void;
  isLastColumn: boolean;
  openMenuTaskId: string | null;
  onTaskMenuToggle: (taskId: string) => void;
  onTaskMenuClose: () => void;
  onDeleteTaskClick: (taskId: number) => void;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  onMoveTaskToColumn?: (taskId: number) => void;
  onMoveTaskToProject?: (taskId: number) => void;
  pendingTaskId?: number | null;
  highlightedTaskId?: number | null;
  columnState?: ColumnState;
  onLoadMore?: () => void | Promise<unknown>;
}) {
  const taskIds = tasks.map((t) => t.id.toString());
  // Drop-zone esplicita per la colonna: @dnd-kit non rileva la SortableColumn
  // come "over" quando è vuota. Un useDroppable dedicato sulla lista task fornisce
  // un'area di drop valida anche in assenza di task.
  const { setNodeRef: setDropRef } = useDroppable({ id: `col-${status.key}` });
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [renameValue, setRenameValue] = useState(status.name);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Click outside handler per il menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onMenuClose();
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, onMenuClose]);

  // Focus e select all quando si entra in modalità rinomina
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      setRenameValue(status.name);
      setRenameError(null);
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming, status.name]);

  const handleSaveRename = async () => {
    const trimmedValue = renameValue.trim();
    if (!trimmedValue) {
      setRenameError("Il nome non può essere vuoto");
      return;
    }
    if (trimmedValue === status.name) {
      onCancelRename();
      return;
    }

    setIsSaving(true);
    setRenameError(null);
    const result = await onSaveRename(trimmedValue);
    setIsSaving(false);

    if (!result.success) {
      setRenameError(result.error || "Errore durante il salvataggio");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancelRename();
    }
  };

  return (
    <div
      className="group rounded-xl bg-[var(--hexa-sidebar-item-active)] backdrop-blur border border-[rgba(106,77,255,0.25)] p-3"
      data-status={status.key}
      style={{ boxShadow: 'var(--hexa-section-shadow)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex items-center gap-2 flex-1 cursor-grab active:cursor-grabbing"
          {...columnListeners}
          {...columnAttributes}
        >
          <div className="flex-1 min-w-0">
            {isRenaming ? (
              <div className="space-y-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={renameValue}
                  onChange={(e) => {
                    setRenameValue(e.target.value);
                    setRenameError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveRename}
                  disabled={isSaving}
                  className="w-full text-sm font-semibold bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] rounded-md px-2 py-1 text-[var(--hexa-text-primary)] focus:outline-none focus:border-[var(--hexa-purple)] transition"
                  style={{ color: status.color }}
                />
                {renameError && (
                  <div className="text-[10px] text-red-400">{renameError}</div>
                )}
              </div>
            ) : (
              <>
                <div className="text-sm font-semibold text-[var(--hexa-text-primary)]" style={{ color: status.color }}>
                  {status.name}
                </div>
                <div className="text-xs text-[var(--hexa-text-muted)]">{columnState?.count ?? tasks.length} task</div>
              </>
            )}
          </div>
        </div>

        {/* Menu 3 puntini - visibile al hover */}
        {isManager && !isRenaming && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMenuToggle();
              }}
              className="w-[26px] h-[26px] rounded-[7px] grid place-items-center text-[var(--hexa-text-muted)] opacity-0 group-hover:opacity-100 hover:bg-[var(--hexa-surface-hover)] hover:text-[var(--hexa-text-primary)] transition-all duration-200"
              aria-label="Menu colonna"
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {/* Dropdown menu */}
            {isMenuOpen && (
              <div
                data-column-menu
                className="absolute right-0 top-full mt-1 w-48 bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-[10px] shadow-lg z-50 overflow-hidden"
                style={{
                  animation: "menuFadeIn 150ms ease-out forwards",
                }}
              >
                <style>{`
                  @keyframes menuFadeIn {
                    from {
                      opacity: 0;
                      transform: translateY(-4px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}</style>

                {/* Modifica colonna (nome + colore) */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditColumn();
                  }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
                >
                  <Pencil className="h-4 w-4" />
                  Modifica colonna
                </button>

                {/* Aggiungi colonna */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddColumn();
                  }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
                >
                  <Plus className="h-4 w-4" />
                  Aggiungi colonna
                </button>

                {/* Elimina colonna */}
                <button
                  type="button"
                  disabled={isLastColumn}
                  title={isLastColumn ? "Deve esistere almeno una colonna" : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLastColumn) return;
                    onDeleteColumn();
                  }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[#ef4444] hover:bg-[var(--hexa-surface-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Trash2 className="h-4 w-4" />
                  Elimina colonna
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div ref={setDropRef} className={`mt-4 min-h-[200px] ${tasks.length > 4 ? "space-y-1.5" : "space-y-3"}`}>
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center h-[200px]">
              <button
                type="button"
                onClick={() => onCreateTask(status.key)}
                className="empty-column-add-btn"
                aria-label="Aggiungi task"
              >
                <Plus className="h-4 w-4" />
              </button>
              <style>{`
                .empty-column-add-btn {
                  width: 36px;
                  height: 36px;
                  border-radius: 10px;
                  border: 1.5px dashed rgba(106, 77, 255, 0.35);
                  background: transparent;
                  display: grid;
                  place-items: center;
                  color: var(--hexa-text-secondary);
                  cursor: pointer;
                  transition: all 0.2s;
                }
                .empty-column-add-btn:hover {
                  background: var(--hexa-purple-glow);
                  border-color: var(--hexa-purple);
                  color: var(--hexa-purple);
                }
              `}</style>
            </div>
          ) : (
            <>
              {tasks.map((t) => (
                <SortableTaskCard
                  key={t.id}
                  task={t}
                  onTaskClick={onTaskClick}
                  isMenuOpen={openMenuTaskId === t.id.toString()}
                  onMenuToggle={() => onTaskMenuToggle(t.id.toString())}
                  onDeleteClick={() => onDeleteTaskClick(t.id)}
                  onToggleCompleted={onToggleCompleted}
                  onMoveTaskToColumn={onMoveTaskToColumn}
                  onMoveTaskToProject={onMoveTaskToProject}
                  isHighlighted={highlightedTaskId === t.id}
                  compact={tasks.length > 4}
                  disabled={pendingTaskId === t.id}
                />
              ))}
              {columnState?.hasMore && onLoadMore && (
                <button
                  type="button"
                  onClick={() => onLoadMore()}
                  disabled={columnState.loading}
                  className="w-full py-1.5 px-3 rounded-md text-xs font-medium
                             bg-[rgba(123,63,255,0.1)] hover:bg-[rgba(123,63,255,0.2)]
                             border border-[rgba(123,63,255,0.3)]
                             text-[var(--hexa-purple)]
                             transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {columnState.loading
                    ? "Caricamento..."
                    : `↓ Carica altri ${Math.max(1, Math.min(10, columnState.count - tasks.length))}`}
                </button>
              )}
              {/* Pulsante + persistente: sempre visibile sotto i task nelle colonne popolate. */}
              <button
                type="button"
                onClick={() => onCreateTask(status.key)}
                className="w-full py-2 px-3 rounded-md text-xs
                           bg-transparent hover:bg-[var(--hexa-purple-glow)]
                           border border-dashed border-[rgba(106,77,255,0.35)]
                           text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-purple)]
                           transition-colors cursor-pointer"
              >
                + Aggiungi task
              </button>
            </>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

function KanbanColumnHeader({ status, taskCount }: { status: TaskStatusDetail; taskCount: number }) {
  return (
    <div className="rounded-xl bg-[var(--hexa-sidebar-item-active)] backdrop-blur border border-[var(--hexa-input-border)] p-3" style={{ boxShadow: 'var(--hexa-section-shadow)' }}>
      <div className="text-sm font-semibold text-[var(--hexa-text-primary)]" style={{ color: status.color }}>
        {status.name}
      </div>
      <div className="text-xs text-[var(--hexa-text-muted)]">{taskCount} task</div>
    </div>
  );
}

function SortableTaskCard({
  task,
  onTaskClick,
  isMenuOpen,
  onMenuToggle,
  onDeleteClick,
  onToggleCompleted,
  onMoveTaskToColumn,
  onMoveTaskToProject,
  isHighlighted,
  compact = false,
  disabled = false,
}: {
  task: Task;
  onTaskClick: (taskId: number) => void;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
  onDeleteClick: () => void;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  onMoveTaskToColumn?: (taskId: number) => void;
  onMoveTaskToProject?: (taskId: number) => void;
  isHighlighted?: boolean;
  compact?: boolean;
  /**
   * FE-5 §8: disabilita drag e interactions quando il task è in fase di
   * spostamento (move pending). Evita doppio invio e inconsistenza visuale.
   */
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id.toString(),
    disabled,
    transition: {
      duration: 200,
      easing: 'ease',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard
        task={task}
        onTaskClick={onTaskClick}
        isMenuOpen={isMenuOpen}
        onMenuToggle={onMenuToggle}
        onDeleteClick={onDeleteClick}
        onToggleCompleted={onToggleCompleted}
        onMoveTaskToColumn={onMoveTaskToColumn}
        onMoveTaskToProject={onMoveTaskToProject}
        isHighlighted={isHighlighted}
        compact={compact}
        isDragging={isDragging}
        disabled={disabled}
      />
    </div>
  );
}

// Layout compatto usato quando la colonna supera i 4 task: bordino priorità,
// titolo troncato, data breve, primo assignee + eventuale "+N".
function CompactCardMarkup({
  task,
  isCompleted,
  isHighlighted,
  onTaskClick,
  onMouseEnter,
  onMouseLeave,
}: {
  task: Task;
  isCompleted: boolean;
  isHighlighted: boolean;
  onTaskClick: (taskId: number) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const taskIsOverdue = !isCompleted && isOverdue(task);
  const accentColor = isCompleted
    ? "#7b3fff"
    : taskIsOverdue
      ? "#ef4444"
      : getPriorityAccentColor(task.priority);

  return (
    <div
      data-task-id={task.id}
      onClick={() => onTaskClick(task.id)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`rounded-lg bg-[var(--hexa-sidebar-item-active)] border border-[rgba(106,77,255,0.25)] hover:border-[var(--hexa-border)] transition px-3 py-2 cursor-grab active:cursor-grabbing ${
        isCompleted ? "opacity-60" : ""
      } ${isHighlighted ? "task-highlight" : ""}`}
      title={task.title}
    >
      {/* Riga 1: bordino priorità + titolo (max 2 righe, poi clamp) */}
      <div className="flex items-start gap-2">
        <span
          className="flex-shrink-0 w-[3px] h-4 mt-0.5 rounded-full"
          style={{ backgroundColor: accentColor }}
          aria-hidden
        />
        <span
          className={`flex-1 text-[13px] font-medium leading-snug text-[var(--hexa-text-primary)] ${
            isCompleted ? "line-through" : ""
          }`}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {task.title}
        </span>
      </div>

      {/* Riga 2: data breve a sinistra + avatar a destra */}
      <div className="flex items-center justify-between gap-2 mt-1.5 pl-[11px]">
        {task.due_date ? (
          <span className="text-[10px] text-[var(--hexa-text-secondary)]">
            {formatDateShort(task.due_date)}
          </span>
        ) : (
          <span aria-hidden />
        )}

        {task.assigned_to && task.assigned_to.length > 0 && (
          <div className="flex -space-x-1 items-center">
            <div
              className={`h-5 w-5 rounded-full ${getAssigneeGradient(task.assigned_to[0].id)} grid place-items-center border border-[var(--hexa-sidebar-item-active)]`}
              title={task.assigned_to[0].username}
            >
              <span className="text-[8px] font-semibold text-[var(--hexa-text-primary)]">
                {getUserInitials(task.assigned_to[0].username)}
              </span>
            </div>
            {task.assigned_to.length > 1 && (
              <span className="text-[9px] font-semibold text-[var(--hexa-text-secondary)] pl-1.5">
                +{task.assigned_to.length - 1}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({
  task,
  onTaskClick,
  isMenuOpen = false,
  onMenuToggle,
  onDeleteClick,
  onToggleCompleted,
  onMoveTaskToColumn,
  onMoveTaskToProject,
  isHighlighted = false,
  compact = false,
  isDragging = false,
  disabled = false,
}: {
  task: Task;
  onTaskClick: (taskId: number) => void;
  isMenuOpen?: boolean;
  onMenuToggle?: () => void;
  onDeleteClick?: () => void;
  onToggleCompleted?: (taskId: number) => Promise<void> | void;
  onMoveTaskToColumn?: (taskId: number) => void;
  onMoveTaskToProject?: (taskId: number) => void;
  isHighlighted?: boolean;
  compact?: boolean;
  isDragging?: boolean;
  disabled?: boolean;
}) {
  const isCompleted = !!task.is_completed;
  const badgeStyles = getPriorityBadgeStyles(task.priority);
  const taskIsOverdue = !isCompleted && isOverdue(task);

  // Hover-expand per le card compatte: dopo 600ms di hover la card passa al layout normale
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (!compact) return;
    if (isDragging) return;
    hoverTimerRef.current = setTimeout(() => {
      setIsHoverExpanded(true);
    }, 600);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsHoverExpanded(false);
  };

  // Se inizia un drag mentre stiamo contando o mentre siamo espansi, collassiamo subito
  useEffect(() => {
    if (isDragging) {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setIsHoverExpanded(false);
    }
  }, [isDragging]);

  // Cleanup al dismount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const renderCompact = compact && !isHoverExpanded;

  if (renderCompact) {
    return (
      <CompactCardMarkup
        task={task}
        isCompleted={isCompleted}
        isHighlighted={isHighlighted}
        onTaskClick={onTaskClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    );
  }

  return (
    <div
      data-task-id={task.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`rounded-xl bg-[var(--hexa-sidebar-item-active)] border hover:border-[var(--hexa-border)] transition-shadow p-3 cursor-grab active:cursor-grabbing hexa-card-shadow ${
        taskIsOverdue
          ? "border-2 border-[#ef4444]"
          : "border border-[rgba(106,77,255,0.3)] dark:border-[var(--hexa-input-border)]"
      } ${isCompleted ? "opacity-60" : ""} ${isHighlighted ? "task-highlight" : ""} ${
        compact && isHoverExpanded
          ? "ring-2 ring-[var(--hexa-purple)] ring-offset-2 ring-offset-[var(--hexa-sidebar-item-active)]"
          : ""
      }`}
      style={taskIsOverdue ? { boxShadow: "0 0 16px rgba(239, 68, 68, 0.25)" } : undefined}
      onClick={() => onTaskClick(task.id)}
    >
      <div className="flex items-center justify-between gap-3">
        {isCompleted ? (
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border bg-[#E8DEFF] dark:bg-[rgba(123,63,255,0.3)] text-[#7c3aed] dark:text-[#7b3fff] border-[#D4C4FF] dark:border-[rgba(123,63,255,0.45)]"
          >
            Completato
          </span>
        ) : (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${badgeStyles.bg} ${badgeStyles.text} ${badgeStyles.border}`}
          >
            {badgeStyles.label}
          </span>
        )}

        {/* Menu 3 puntini con dropdown */}
        <div className="relative" data-task-menu>
          <button
            type="button"
            className="h-8 w-8 rounded-lg grid place-items-center text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)] transition"
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle?.();
            }}
            aria-label="Azioni task"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-40 bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-[10px] shadow-lg z-50 overflow-hidden"
              style={{
                animation: "menuFadeIn 150ms ease-out forwards",
              }}
            >
              <style>{`
                @keyframes menuFadeIn {
                  from {
                    opacity: 0;
                    transform: translateY(-4px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>

              {/* Modifica */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuToggle?.();
                  onTaskClick(task.id);
                }}
                className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
              >
                <Pencil className="h-4 w-4" />
                Modifica
              </button>

              {/* Sposta nella colonna... (FE-5 §8): alternativa accessibile al drag */}
              {onMoveTaskToColumn && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMenuToggle?.();
                    onMoveTaskToColumn(task.id);
                  }}
                  disabled={disabled}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowRightCircle className="h-4 w-4" />
                  Sposta nella colonna...
                </button>
              )}

              {/* Sposta in un altro progetto (FE-5 §8) */}
              {onMoveTaskToProject && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMenuToggle?.();
                    onMoveTaskToProject(task.id);
                  }}
                  disabled={disabled}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FolderInput className="h-4 w-4" />
                  Sposta in un altro progetto
                </button>
              )}

              {/* Toggle completato */}
              {onToggleCompleted && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMenuToggle?.();
                    onToggleCompleted(task.id);
                  }}
                  className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
                >
                  {isCompleted ? (
                    <>
                      <RotateCcw className="h-4 w-4" />
                      Riprendi
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Completa
                    </>
                  )}
                </button>
              )}

              {/* Elimina */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteClick?.();
                }}
                className="w-full flex items-center gap-2 px-3.5 py-2 text-[13px] text-[#ef4444] hover:bg-[var(--hexa-surface-hover)] transition"
              >
                <Trash2 className="h-4 w-4" />
                Elimina
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div
          className={`text-sm font-semibold leading-snug text-[var(--hexa-text-primary)] ${isCompleted ? "line-through" : ""}`}
        >
          {task.title}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-[var(--hexa-text-secondary)]">
          <span className="inline-flex items-center gap-1">
            <Paperclip className="h-4 w-4 text-[var(--hexa-text-secondary)]" />
            {task.attachments?.length ?? 0}
          </span>

          {taskIsOverdue ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold border bg-[#FFD4D4] dark:bg-[rgba(239,68,68,0.3)] text-[#dc2626] dark:text-[#ef4444] border-[#FFB0B0] dark:border-[rgba(239,68,68,0.45)]"
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-[#dc2626] dark:bg-[#ef4444]"
                style={{ animation: "pulse 1.5s ease-in-out infinite" }}
              />
              {formatDate(task.due_date)} · Scaduto
            </span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-4 w-4 text-[var(--hexa-text-secondary)]" />
              {formatDate(task.due_date)}
            </span>
          )}
        </div>

        {task.assigned_to && task.assigned_to.length > 0 ? (
          <div className="flex -space-x-2">
            {task.assigned_to.slice(0, 3).map((assignee) => (
              <div
                key={assignee.id}
                className={`h-6 w-6 rounded-full ${getAssigneeGradient(assignee.id)} grid place-items-center border-2 border-[var(--hexa-sidebar-item-active)]`}
                title={assignee.username}
              >
                <span className="text-[9px] font-semibold text-[var(--hexa-text-primary)]">
                  {getUserInitials(assignee.username)}
                </span>
              </div>
            ))}
            {task.assigned_to.length > 3 && (
              <div className="h-6 w-6 rounded-full bg-[var(--hexa-border)] grid place-items-center border-2 border-[var(--hexa-sidebar-item-active)]">
                <span className="text-[9px] font-semibold text-[var(--hexa-text-primary)]">
                  +{task.assigned_to.length - 3}
                </span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

